import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import * as Sentry from '@sentry/react';
import { trackEvent } from './analytics';

/**
 * Initialize web vitals monitoring
 * Tracks Core Web Vitals and sends them to Sentry and Plausible
 * Note: FID is deprecated in web-vitals v4, replaced by INP
 */
export const initPerformanceMonitoring = () => {
  if (!import.meta.env.PROD) {
    console.log('[Performance] Running in dev mode - metrics will be logged only');
    return;
  }

  // Report each metric as it becomes available
  onCLS(sendToAnalytics);
  onFCP(sendToAnalytics);
  onLCP(sendToAnalytics);
  onTTFB(sendToAnalytics);
  onINP(sendToAnalytics);

  console.log('[Performance] Web Vitals monitoring initialized');
};

/**
 * Send web vital metrics to analytics platforms
 */
function sendToAnalytics(metric: Metric) {
  const { name, value, rating, navigationType } = metric;

  // Round the value for cleaner reporting
  const roundedValue = Math.round(name === 'CLS' ? value * 1000 : value);

  if (import.meta.env.PROD) {
    // Send to Sentry as a measurement
    Sentry.setMeasurement(name, roundedValue, getUnit(name));

    // Also capture as a message for visibility
    Sentry.captureMessage(`Web Vital: ${name}`, {
      level: rating === 'good' ? 'info' : rating === 'needs-improvement' ? 'warning' : 'error',
      tags: {
        web_vital: name,
        web_vital_rating: rating,
      },
      extra: {
        value: roundedValue,
        rating,
        navigationType,
      },
    });

    // Send to Plausible as a custom event
    trackEvent('Web Vitals', {
      metric: name,
      value: roundedValue,
      rating,
    });
  } else {
    // Log in development
    console.log(`[Performance] ${name}:`, {
      value: roundedValue,
      rating,
      navigationType,
    });
  }
}

/**
 * Get the unit for a web vital metric
 */
function getUnit(metricName: string): string {
  switch (metricName) {
    case 'CLS':
      return '';
    case 'FID':
    case 'FCP':
    case 'LCP':
    case 'TTFB':
    case 'INP':
      return 'millisecond';
    default:
      return '';
  }
}

/**
 * Helper to manually track custom performance metrics
 */
export const trackPerformance = (metricName: string, duration: number, metadata?: Record<string, any>) => {
  if (import.meta.env.PROD) {
    // Send to Sentry
    Sentry.setMeasurement(metricName, duration, 'millisecond');
    
    if (metadata) {
      Sentry.captureMessage(`Performance: ${metricName}`, {
        level: 'info',
        extra: {
          duration,
          ...metadata,
        },
      });
    }

    // Send to Plausible
    trackEvent('Performance Metric', {
      metric: metricName,
      duration,
      ...metadata,
    });
  } else {
    console.log(`[Performance] ${metricName}: ${duration}ms`, metadata);
  }
};

/**
 * Measure and track a specific operation's performance
 * Usage:
 * const stopTimer = measureOperation('fetchActivities');
 * // ... do work ...
 * stopTimer({ resultsCount: 10 });
 */
export const measureOperation = (operationName: string) => {
  const startTime = performance.now();
  
  return (metadata?: Record<string, any>) => {
    const duration = performance.now() - startTime;
    trackPerformance(operationName, duration, metadata);
  };
};

/**
 * Track long tasks that block the main thread
 * This helps identify performance bottlenecks
 */
export const initLongTaskMonitoring = () => {
  if (!import.meta.env.PROD) {
    return;
  }

  // Check if PerformanceObserver is supported
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Only report tasks longer than 100ms
        if (entry.duration > 100) {
          Sentry.captureMessage('Long Task Detected', {
            level: 'warning',
            tags: {
              performance_issue: 'long_task',
            },
            extra: {
              duration: Math.round(entry.duration),
              startTime: Math.round(entry.startTime),
              name: entry.name,
            },
          });

          trackEvent('Long Task', {
            duration: Math.round(entry.duration),
          });
        }
      }
    });

    observer.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    // Long task API not supported, silently fail
    console.log('[Performance] Long task monitoring not supported');
  }
};

/**
 * Track resource loading performance
 * Useful for identifying slow assets
 */
export const trackResourceTiming = (resourceUrl: string) => {
  if (!import.meta.env.PROD) {
    return;
  }

  try {
    const resources = performance.getEntriesByName(resourceUrl);
    if (resources.length > 0) {
      const resource = resources[0] as PerformanceResourceTiming;
      const duration = resource.responseEnd - resource.startTime;

      if (duration > 1000) { // Only track resources that take > 1s
        trackPerformance('Resource Load Time', duration, {
          url: resourceUrl,
          type: resource.initiatorType,
        });
      }
    }
  } catch (e) {
    // Silently fail if performance API is not available
  }
};
